import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ExistingUserData } from '@/domains/user/components/ExistingUserInfoCard/types';
import { CreateInsuredAutomaticallyBodyType, CreateInsuredManuallyBodyType } from '../sagas/types';
import { CreateInsuredOptionsResponse } from '../types/api/responses/CreateInsuredOptionsResponse';
import { CreateInsuredFieldKey, CreateInsuredFieldValue } from './types/createInsuredTypes';
import { AddInsuredModalSteps } from '@/features/serviceModal/components/AddInsuredModal/enums';
import { CreateEntityTypesEnum } from '@/enums/CreateEntityTypesEnum';
import { createInsuredInitialState } from './constants/createInsuredConstants';

const createInsuredSlice = createSlice({
  name: 'createInsured',
  initialState: createInsuredInitialState,
  reducers: {
    changeCreateInsuredType: (state, action: PayloadAction<CreateEntityTypesEnum>) => {
      state.createInsuredType = action.payload;
    },
    updateCreateInsuredStepValues: <K extends CreateInsuredFieldKey>(
      state,
      action: PayloadAction<{ step: K; value: CreateInsuredFieldValue<K> }>,
    ) => {
      const { step, value } = action.payload;
      state.createInsuredSteps[step] = value;
    },
    resetCreateInsuredState: () => createInsuredInitialState,
    setUserForInsuredCreate: (state, action: PayloadAction<ExistingUserData | null>) => {
      state.userForInsuredCreate = action.payload;
    },
    setUsersListForInsuredCreate: (state, action: PayloadAction<ExistingUserData[] | null>) => {
      state.usersListForInsuredCreate = action.payload;
    },
    getCreateInsuredExistingUserById: (_, _action: PayloadAction<string>) => {},
    createInsured: (
      state,
      _action: PayloadAction<CreateInsuredManuallyBodyType | CreateInsuredAutomaticallyBodyType>,
    ) => {
      state.statuses.isCreateInsuredLoading = true;
    },
    setCreateInsuredStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isCreateInsuredLoading = payload;
    },
    getCreateInsuredOptions: (state) => {
      state.statuses.isSelectOptionsLoading = true;
    },
    getCreateInsuredOptionsSuccess: (state, { payload }: PayloadAction<CreateInsuredOptionsResponse>) => {
      state.selectOptions = payload;
    },
    setCreateInsuredOptionsStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isSelectOptionsLoading = payload;
    },
  },
  selectors: {
    selectCreateInsuredSteps: (state) => state.createInsuredSteps,
    selectUserForInsuredCreate: (state) => state.userForInsuredCreate,
    selectUsersListForInsuredCreate: (state) => state.usersListForInsuredCreate,
    selectCreateInsuredType: (state) => state.createInsuredType,
    selectCreateInsuredSelectOptionsIsLoading: (state) => state.statuses.isSelectOptionsLoading,
    selectCreateInsuredIsLoading: (state) => state.statuses.isCreateInsuredLoading,
    selectCreateInsuredSelectOptions: (state) => state.selectOptions,
    selectCreateInsuredAddressDetails: (state) => state.createInsuredSteps[AddInsuredModalSteps.AddressDetails],
  },
});

export const {
  changeCreateInsuredType,
  getCreateInsuredExistingUserById,
  resetCreateInsuredState,
  setUserForInsuredCreate,
  setUsersListForInsuredCreate,
  updateCreateInsuredStepValues,
  createInsured,
  getCreateInsuredOptions,
  getCreateInsuredOptionsSuccess,
  setCreateInsuredStatus,
  setCreateInsuredOptionsStatus,
} = createInsuredSlice.actions;

export const {
  selectCreateInsuredSteps,
  selectUserForInsuredCreate,
  selectUsersListForInsuredCreate,
  selectCreateInsuredType,
  selectCreateInsuredSelectOptions,
  selectCreateInsuredSelectOptionsIsLoading,
  selectCreateInsuredIsLoading,
  selectCreateInsuredAddressDetails,
} = createInsuredSlice.selectors;

export const createInsuredReducer = createInsuredSlice.reducer;
export const createInsuredReducerPath = createInsuredSlice.reducerPath;
